import React, { useState, useEffect, useRef } from 'react';
import { Mail, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/simple-toaster';
import { useOrganizationSettings, useUpdateOrganizationSettings } from '@/hooks/use-settings';

export function EmailIdentityCard() {
  const { data: settings } = useOrganizationSettings();
  const updateSettings = useUpdateOrganizationSettings();

  const [fromName, setFromName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Track whether the user has made local edits so that a query refetch
  // triggered by another card's save doesn't silently discard our changes.
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (settings && !dirtyRef.current) {
      setFromName(settings.email_from_name || '');
      setFromAddress(settings.email_from_address || '');
      setReplyTo(settings.email_reply_to || '');
    }
  }, [settings]);

  useEffect(() => {
    if (settings) {
      const nameChanged = fromName !== (settings.email_from_name || '');
      const addressChanged = fromAddress !== (settings.email_from_address || '');
      const replyChanged = replyTo !== (settings.email_reply_to || '');
      const dirty = nameChanged || addressChanged || replyChanged;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasChanges(dirty);
      dirtyRef.current = dirty;
    }
  }, [fromName, fromAddress, replyTo, settings]);

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        email_from_name: fromName,
        email_from_address: fromAddress,
        email_reply_to: replyTo,
      });
      dirtyRef.current = false;
      toast.success('Email Identity Saved', {
        description: 'Your email sender settings have been updated.',
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to save email identity settings. Please try again.',
      });
    }
  };

  const handleReset = () => {
    if (settings) {
      dirtyRef.current = false;
      setFromName(settings.email_from_name || '');
      setFromAddress(settings.email_from_address || '');
      setReplyTo(settings.email_reply_to || '');
    }
  };

  const isVerified = settings?.email_sender_verified ?? false;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Identity
            </CardTitle>
            <CardDescription>
              Configure the sender name and address for transactional emails sent on behalf of your organization
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {hasChanges && (
              <Button variant="outline" size="sm" onClick={handleReset}>
                Reset
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={!hasChanges || updateSettings.isPending}>
              {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Verification Status Banner */}
        {isVerified ? (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
            <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-900">Domain Verified</p>
              <p className="text-sm text-green-700">
                Emails are sent from your organization&apos;s own sender identity.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-900">Domain Not Verified</p>
              <p className="text-sm text-amber-700">
                Emails are sent from the platform default address. To send from your own domain,
                please email <span className="font-medium">support@h3ight.com</span> to verify your domain.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email-from-name">From Name</Label>
            <Input
              id="email-from-name"
              type="text"
              placeholder="e.g. Apex Rope Access"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Display name on outgoing emails. Defaults to your organization name if blank.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email-from-address">From Address</Label>
            <Input
              id="email-from-address"
              type="email"
              placeholder="e.g. billing@apexropeaccess.com"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              {isVerified
                ? 'Verified sender address for outbound emails.'
                : 'Set your desired sender address. It will be used once your domain is verified by support.'}
            </p>
          </div>

          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="email-reply-to">Reply-To Address</Label>
            <Input
              id="email-reply-to"
              type="email"
              placeholder="e.g. support@apexropeaccess.com"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              When recipients reply to your emails, their response will be directed to this address.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
